import { DECK_POWER_TIER_AT, type DeckPowerTone } from 'features/catalog/rarity-config';
import { pickCpuInput } from 'features/battle/ai';
import type { CpuDifficulty } from 'features/battle/ai-config';
import { createBattle, reduceBattle } from 'features/battle/engine';
import { cachedSampleDeckPower } from 'features/battle/sample-deck-power';
import { SAMPLE_BATTLE_DECKS } from 'features/battle/sample-decks';
import { BATTLE_DECK_SIZE, type BattleState } from 'features/battle/types';

/** Impossible win rates stay unknown and are never simulated. */
type RatedCpuDifficulty = Exclude<CpuDifficulty, 'impossible'>;
/** Games the estimate plays against each sample deck. Easy and hard both use this count. */
export const DECK_POWER_GAMES_PER_SAMPLE = 10;
/** Shared opening seed so Easy/Hard estimates replay the same shuffles and coin tosses. */
export const DECK_POWER_SEED = 1;
/** Gap between matchup seeds so the ten games per sample deck stay distinct. */
export const DECK_POWER_GAME_STRIDE = 9973;
/** Deck power tier Randomize Deck rerolls toward, per opponent. Tougher CPUs settle for a lower tier. */
const RECOMMENDED_DECK_TIER: Record<RatedCpuDifficulty, DeckPowerTone> = { easy: 'good', hard: 'fair' };
/** Randomize Deck rerolls the player's own badges until the estimate reaches this win rate. */
export const recommendedDeckMinWinRate = (opponent: RatedCpuDifficulty): number =>
  DECK_POWER_TIER_AT[RECOMMENDED_DECK_TIER[opponent]];
/** Randomize Deck always compares at least this many rolls and keeps the best. */
export const RECOMMENDED_DECK_MIN_ROLLS = 1;
/** Rerolls before Randomize Deck settles for the best deck it found. */
export const RECOMMENDED_DECK_MAX_ROLLS = 10;
/** Randomize Deck scouts with one game per sample so the search can animate. The shown % still uses the full estimate. */
export const recommendedDeckGames = (_opponent?: RatedCpuDifficulty): number => SAMPLE_BATTLE_DECKS.length;
const MAX_PLIES = 100;

/** Ten games against every sample deck. Easy and hard estimates use the same match count. */
export const deckPowerGames = (_opponent?: RatedCpuDifficulty): number =>
  SAMPLE_BATTLE_DECKS.length * DECK_POWER_GAMES_PER_SAMPLE;

/** One point for a win, half for a draw. Unfinished games count as draws. */
const gameScore = (winner: BattleState['winner']): number => (winner === 0 ? 1 : winner === 1 ? 0 : 0.5);

/** Same 12 badges always shuffle from the same order, even if the picker stored them differently. */
const orderedDeck = (ids: readonly string[]): string[] => [...ids].sort();

/** Let the browser paint between estimate games so deck swaps and Randomize do not hitch. Randomize must call `rateDeckPowerAsync`, not the sync `rateDeckPower`. */
const yieldDeckPower = (): Promise<void> =>
  new Promise((resolve) => {
    const later = () => {
      if (typeof window !== 'undefined') {
        window.setTimeout(resolve, 0);
        return;
      }
      setTimeout(resolve, 0);
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(later);
    else later();
  });

const playAutoGame = (
  playerDeck: readonly string[],
  opponentDeck: readonly string[],
  seed: number,
  opponent: CpuDifficulty
): number => {
  let state = createBattle({
    decks: [orderedDeck(playerDeck), orderedDeck(opponentDeck)],
    names: ['Hard CPU', 'CPU'],
    seed,
    cpu: 1,
    cpuDifficulty: opponent,
    autoplay: true,
    shuffle: true,
    coinToss: true,
  });
  for (let step = 0; step < MAX_PLIES && state.phase !== 'game_over'; step += 1) {
    // Your deck is always a Hard CPU. Easy estimates face Easy; hard estimates face Hard.
    const actor = state.pending?.player ?? state.activePlayer;
    const input = pickCpuInput(state, actor === 0 ? 'hard' : opponent);
    if (!input) break;
    const next = reduceBattle(state, input);
    if (next === state) break;
    state = next;
  }
  return gameScore(state.phase === 'game_over' ? state.winner : 'tie');
};

const playDeckPowerGame = (playerDeck: readonly string[], played: number, opponent: CpuDifficulty): number => {
  const sample = SAMPLE_BATTLE_DECKS[Math.floor(played / DECK_POWER_GAMES_PER_SAMPLE)];
  if (!sample) {
    throw new Error(`Deck power game ${played} is outside the sample field.`);
  }
  return playAutoGame(playerDeck, sample.badgeIds, DECK_POWER_SEED + played * DECK_POWER_GAME_STRIDE, opponent);
};

const requireFullDeck = (playerDeck: readonly string[]): void => {
  if (playerDeck.length !== BATTLE_DECK_SIZE) {
    throw new Error(`Deck power needs exactly ${BATTLE_DECK_SIZE} badges.`);
  }
};

const fieldGames = (): number => SAMPLE_BATTLE_DECKS.length * DECK_POWER_GAMES_PER_SAMPLE;

const savedSampleDeckPower = (
  playerDeck: readonly string[],
  games: number | undefined,
  opponent: RatedCpuDifficulty
): number | null => {
  const available = fieldGames();
  if (Math.min(games ?? available, available) !== available) return null;
  return cachedSampleDeckPower(playerDeck, opponent, {
    gamesPerSample: DECK_POWER_GAMES_PER_SAMPLE,
    seed: DECK_POWER_SEED,
    stride: DECK_POWER_GAME_STRIDE,
  });
};

/** Instant Easy/Hard % for a named sample list. `null` means the UI should estimate. */
export const knownSampleDeckPower = (playerDeck: readonly string[], opponent: CpuDifficulty): number | null => {
  if (opponent === 'impossible' || playerDeck.length !== BATTLE_DECK_SIZE) return null;
  return savedSampleDeckPower(playerDeck, undefined, opponent);
};

/**
 * Win rate for `playerDeck` (played by a Hard CPU) against an `opponent`-difficulty CPU.
 * Plays `DECK_POWER_GAMES_PER_SAMPLE` games against every sample deck, the same pool the CPU match picks from.
 * Seeds are fixed, so the same deck and opponent always replay the same matchups and percentage.
 * Named sample lists reuse the Easy/Hard percentages saved by the sample-decks field test.
 */
export const rateDeckPower = (
  playerDeck: readonly string[],
  games?: number,
  opponent: RatedCpuDifficulty = 'easy'
): number => {
  requireFullDeck(playerDeck);
  const saved = savedSampleDeckPower(playerDeck, games, opponent);
  if (saved != null) return saved;
  const available = fieldGames();
  const cap = Math.min(games ?? available, available);
  let score = 0;
  for (let played = 0; played < cap; played += 1) {
    score += playDeckPowerGame(playerDeck, played, opponent);
  }
  return Math.round((score / cap) * 100);
};

/**
 * Same matchups as `rateDeckPower`, one game at a time so the UI can keep animating.
 * Returns `null` when `isCancelled` flips during the run.
 */
export const rateDeckPowerAsync = async (
  playerDeck: readonly string[],
  games?: number,
  opponent: RatedCpuDifficulty = 'easy',
  isCancelled: () => boolean = () => false
): Promise<number | null> => {
  requireFullDeck(playerDeck);
  const saved = savedSampleDeckPower(playerDeck, games, opponent);
  if (saved != null) return isCancelled() ? null : saved;
  const available = fieldGames();
  const cap = Math.min(games ?? available, available);
  let score = 0;
  for (let played = 0; played < cap; played += 1) {
    if (isCancelled()) return null;
    score += playDeckPowerGame(playerDeck, played, opponent);
    if (played + 1 < cap) await yieldDeckPower();
  }
  if (isCancelled()) return null;
  return Math.round((score / cap) * 100);
};
